import React, { useState, useEffect, useMemo } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, FileDown } from 'lucide-react';
import { getGpsWeekSummary, getGpsWeekSummaryScenarios } from '../../api';
import { downloadBlob, renderReportPdf } from '../../pdfExport';
import { useUrlParam } from '../../hooks/useUrlParam';

// ── Metric metadata — labels/units match GPSData.js's GAUGE_METRICS and MatchChronic.js's
// existing HMLD label exactly, so this page never invents a second vocabulary for the same
// metrics shown elsewhere in GPS & Load. ──────────────────────────────────────────────────
const METRIC_ORDER = ['TD', 'HID', 'HMLD', 'SD', 'SA'];
const METRIC_META = {
  TD:   { label: 'Total Distance', short: 'TD',  unit: 'm' },
  HID:  { label: 'HID >19.8km/h',  short: 'HID', unit: 'm' },
  HMLD: { label: 'High Metabolic Load Distance', short: 'HMLD', unit: 'm' },
  SD:   { label: 'Sprint >25.2km/h', short: 'SD', unit: 'm' },
  SA:   { label: 'Short Actions',  short: 'A/D', unit: '' },
};
// "Volume" (how much) vs "intensity/quality" (how hard) split, used only by the verdict rule
// set below — matches the build prompt's own worked example (TD/HID above, HMLD/SD below =
// "volume high, intensity low").
const VOLUME_METRICS = ['TD', 'HID'];
const INTENSITY_METRICS = ['HMLD', 'SD', 'SA'];

const COLOR = { green: '#10b981', amber: '#f59e0b', red: '#ef4444' };
// Fixed, theme-independent grey for the MD secondary (<60 min) series specifically — NOT
// var(--text-dimmed), which resolves to a pale #94a3b8 tuned for legibility on this app's dark
// background and is nearly invisible against day theme's white panels (confirmed: reads as a
// near-blank line/dot in light mode). A mid-tone hex reads clearly on both themes.
const GREY_SERIES = '#6b7280';
const RISK_META = {
  safe:     { label: 'safe',     color: '#10b981' },
  caution:  { label: 'caution',  color: '#f59e0b' },
  red_flag: { label: 'red flag', color: '#ef4444' },
  // Labelled "no zone", not "no data" — pillarScores.js's scoreEwmaRatio only assigns
  // safe/caution/red_flag when the acute:chronic ratio is >=0.8; a ratio below that (or not yet
  // computable) is left unbadged on purpose ("no undertrained label" rule), so most of a squad
  // training at a normal load lands here even though their EWMA history is real and visible on
  // the EWMA page. Key stays `noData` (matches the API field) — only the label changes.
  noData:   { label: 'no zone',  color: 'var(--text-dimmed)' },
};

// ── Formatting helpers ──────────────────────────────────────────────────────────────────────
const fmtNum = v => v == null ? '—' : (Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : (Number.isInteger(v) ? String(v) : v.toFixed(1)));
const fmtRange = (lo, hi) => `${fmtNum(lo)}–${fmtNum(hi)}`;
const fmtDev = dev => (dev == null || dev === 0) ? 'in range' : `${dev > 0 ? '+' : ''}${dev.toFixed(1)}%`;
const fmtShortDate = d => { if (!d) return ''; const [, m, dd] = d.split('-'); return `${dd}/${m}`; };
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dowShort = d => DOW[new Date(`${d}T00:00:00Z`).getUTCDay()];
// Same signed-%-past-nearest-bound rule as gpsWeekSummary.js's devOf, duplicated here (page-
// local, like this file's other small pure helpers) for the pacing chart's per-point tooltip —
// that function lives in the backend module and isn't imported into this page.
const pacingDevOf = (actual, lo, hi) => actual > hi ? ((actual - hi) / hi) * 100 : actual < lo ? ((actual - lo) / lo) * 100 : 0;

// ── Verdict banner — small explicit rule set, not an insight engine. First pass; expect
// iteration once real output exists (open for Frederik's sign-off per the build prompt). ────
function generateVerdict(metrics) {
  const status = k => metrics[k]?.status;
  const aboveKeys = METRIC_ORDER.filter(k => status(k) === 'above');
  const belowKeys = METRIC_ORDER.filter(k => status(k) === 'below');
  const outCount = aboveKeys.length + belowKeys.length;
  // "some X and none of the opposite" is NOT the same as "every metric is X" — with 4 in-range
  // and 1 below, that reads as "below across the board" even though the week was clean. Counts,
  // not existence checks, so a single close miss can't be read as a squad-wide failure pattern.
  const volAboveCount = VOLUME_METRICS.filter(k => status(k) === 'above').length;
  const volBelowCount = VOLUME_METRICS.filter(k => status(k) === 'below').length;
  const intAboveCount = INTENSITY_METRICS.filter(k => status(k) === 'above').length;
  const intBelowCount = INTENSITY_METRICS.filter(k => status(k) === 'below').length;
  const volAbovePattern = volAboveCount > 0 && volBelowCount === 0;
  const volBelowPattern = volBelowCount > 0 && volAboveCount === 0;
  const intAbovePattern = intAboveCount > 0 && intBelowCount === 0;
  const intBelowPattern = intBelowCount > 0 && intAboveCount === 0;

  let tag, headline;
  if (outCount === 0) {
    tag = 'On plan';
    headline = 'Every load metric is inside its planned range through today.';
  } else if (outCount === 1) {
    // One metric out of five, everything else clean — still a good week, not a failure. Say so
    // plainly rather than defaulting to a generic "mixed"/"below" framing over one data point.
    const k = aboveKeys[0] || belowKeys[0];
    tag = 'On plan · one metric to watch';
    headline = `${METRIC_META[k].label} is the only metric outside its range (${fmtDev(metrics[k].dev)}) — every other metric is on plan.`;
  } else if (outCount >= 2 && volAbovePattern && intBelowPattern) {
    tag = 'Volume high · intensity low';
    headline = 'The squad has run past the top of the volume metrics while sitting under the intensity/quality metrics — long but metabolically flat.';
  } else if (outCount >= 2 && volBelowPattern && intAbovePattern) {
    tag = 'Volume low · intensity high';
    headline = 'Total output is under plan while the intensity/quality metrics are running hot — short, sharp sessions rather than sustained volume.';
  } else if (aboveKeys.length === METRIC_ORDER.length) {
    tag = 'Above plan across the board';
    headline = 'Every load metric is running above its planned range this week.';
  } else if (belowKeys.length === METRIC_ORDER.length) {
    tag = 'Below plan across the board';
    headline = 'Every load metric is under its planned range this week.';
  } else if (aboveKeys.length > belowKeys.length) {
    tag = 'Mostly above plan';
    headline = `${aboveKeys.length} of ${METRIC_ORDER.length} metrics are running above plan; the rest are in range or below.`;
  } else if (belowKeys.length > aboveKeys.length) {
    tag = 'Mostly below plan';
    headline = `${belowKeys.length} of ${METRIC_ORDER.length} metrics are running under plan; the rest are in range or above.`;
  } else {
    tag = 'Mixed';
    headline = 'The week is a mix of metrics above and below plan, without a clean volume/intensity pattern.';
  }
  const outOfBand = METRIC_ORDER.filter(k => status(k) !== 'in range').map(k => `${METRIC_META[k].label} (${fmtDev(metrics[k].dev)})`);
  const detail = outCount > 1 && outOfBand.length ? `Out of range: ${outOfBand.join(', ')}.` : null;
  return { tag, sentences: detail ? [headline, detail] : [headline] };
}

// ── Pacing chart cumulative derivation (client-side, from the per-day series the endpoint
// already returns — avoids a second cumulative representation server-side). Mirrors
// gpsWeekSummary.js's own projection formula exactly: remaining days land at their own band's
// mid-point, added sequentially onto actual-to-date. ────────────────────────────────────────
function buildPacing(series) {
  let loCum = 0, hiCum = 0, actCum = 0, lastActualIdx = -1;
  const rows = series.map((d, i) => {
    if (d.lo != null) { loCum += d.lo; hiCum += d.hi; }
    if (d.actual != null) { actCum += d.actual; lastActualIdx = i; }
    return { ...d, loCum, hiCum, actCum };
  });
  let proj = lastActualIdx >= 0 ? rows[lastActualIdx].actCum : 0;
  const projValues = rows.map((d, i) => {
    if (i <= lastActualIdx) return null;
    if (d.lo != null) proj += (d.lo + d.hi) / 2;
    return proj;
  });
  return { rows, lastActualIdx, projValues };
}

// MD-only grey series: a starter and an unused sub are both "on plan" but cover wildly
// different ground, so MD gets a second, band-less dot for the <60-minute group (see
// gpsWeekSummary.js's MD_PRIMARY_MINUTES_THRESHOLD). The grey line shares the black line's
// cumulative total up through MD-1 (no split exists yet), forks at MD to the <60 group's own
// mean, then — because there's only one population again from MD+1 on — runs PARALLEL to the
// black line for the rest of the week: same day-to-day deltas, permanently offset by the gap
// that opened at MD. Returns null whenever there's nothing to draw (no MD in this scenario, or
// the <60 group is empty) so the caller can render exactly one dot, not an orphaned line.
function buildGreyPacing(rows) {
  const mdIndex = rows.findIndex(r => r.label === 'MD');
  if (mdIndex < 0) return null;
  const secondary = rows[mdIndex].secondary;
  if (!secondary || secondary.n === 0) return null;

  const preMdCum = mdIndex > 0 ? rows[mdIndex - 1].actCum : 0;
  const greyAtMD = preMdCum + secondary.actual;
  const values = rows.map((r, i) => {
    if (i < mdIndex - 1) return null;
    if (i < mdIndex) return r.actCum; // shared with black up to and including MD-1
    return greyAtMD + (r.actCum - rows[mdIndex].actCum); // forks at MD, parallel-shifted after
  });
  return { mdIndex, greyAtMD, n: secondary.n, values };
}

// ── Small shared bits ────────────────────────────────────────────────────────────────────────
const PANEL = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', marginBottom: 14 };
const PH = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' };
const PH_H2 = { margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' };
const PH_SUB = { fontSize: 11.5, color: 'var(--text-muted)' };
const NOTE = { fontSize: 11, color: 'var(--text-muted)', marginTop: 11, lineHeight: 1.5, borderLeft: '2px solid var(--border)', paddingLeft: 9 };

function Seg({ options, value, onChange }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden' }}>
      {options.map((o, i) => (
        <button key={o.value} onClick={() => onChange(o.value)}
          style={{
            border: 'none', borderRight: i < options.length - 1 ? '1px solid var(--border-strong)' : 'none',
            background: value === o.value ? '#3b82f6' : 'var(--bg-card)', color: value === o.value ? '#fff' : 'var(--text-secondary)',
            padding: '6px 12px', fontSize: 12, fontWeight: value === o.value ? 600 : 500, cursor: 'pointer', fontFamily: 'inherit',
          }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Scenario strip ──────────────────────────────────────────────────────────────────────────
function ScenarioStrip({ data }) {
  const { scenario, today, days } = data;
  return (
    <div style={PANEL}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
            Scenario {scenario.scenario}
            {!scenario.isCurrentlyActive && <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12, marginLeft: 8 }}>· not currently active</span>}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
            {fmtShortDate(scenario.startDate)} – {fmtShortDate(scenario.endDate)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flex: 1, minWidth: 320, flexWrap: 'wrap' }}>
          {days.map(d => {
            const isToday = d.date === today;
            const isFuture = d.date > today;
            const isMatch = d.label === 'MD';
            const isRest = d.rest;
            const cancelled = d.cancelled;
            let bg = 'var(--bg-app)', border = 'var(--border)';
            if (cancelled) { bg = 'var(--bg-app)'; border = 'var(--border)'; }
            else if (isMatch) { bg = '#14181f'; border = '#14181f'; }
            else if (isToday) { border = '#3b82f6'; }
            let statusText;
            if (cancelled) statusText = 'cancelled';
            else if (isRest) statusText = 'rest';
            else if (isFuture) statusText = isMatch ? 'upcoming' : 'planned';
            else if (isToday) statusText = 'today';
            else statusText = d.recorded ? '✓ done' : 'pending';
            return (
              <div key={d.date} style={{
                flex: 1, minWidth: 68, border: `${isToday ? 2 : 1}px solid ${border}`, borderRadius: 7,
                borderStyle: cancelled ? 'dashed' : 'solid', padding: '7px 6px', textAlign: 'center', background: bg,
                opacity: isFuture && !isMatch ? 0.6 : 1,
              }} title={cancelled ? 'Cancelled — target dropped from the plan' : undefined}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3, color: isMatch ? '#fff' : 'var(--text-primary)', textDecoration: cancelled ? 'line-through' : 'none' }}>{d.label}</div>
                <div style={{ fontSize: 10, color: isMatch ? '#9aa3b0' : 'var(--text-dimmed)', marginTop: 1, textDecoration: cancelled ? 'line-through' : 'none' }}>{dowShort(d.date)} {fmtShortDate(d.date)}</div>
                <div style={{ fontSize: 9.5, marginTop: 5, letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 700, color: isToday ? '#3b82f6' : isMatch ? '#9aa3b0' : 'var(--text-muted)' }}>{statusText}</div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={NOTE}>
        A <b>cancelled</b> session drops its band out of both plan-to-date and the full-week range, so the squad is only judged against the plan that actually stood. The day stays visible here, struck through, with its original band on hover.
      </div>
    </div>
  );
}

// ── Verdict banner ───────────────────────────────────────────────────────────────────────────
function VerdictBanner({ metrics }) {
  const { tag, sentences } = useMemo(() => generateVerdict(metrics), [metrics]);
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', borderLeft: '4px solid #f59e0b', background: 'rgba(245,158,11,0.08)', borderRadius: 8, padding: '13px 16px', marginBottom: 14 }}>
      <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', background: '#f59e0b', color: '#1c1917', padding: '4px 9px', borderRadius: 5, whiteSpace: 'nowrap', marginTop: 1 }}>{tag}</span>
      <div>
        {sentences.map((s, i) => (
          <p key={i} style={{ margin: i === 0 ? 0 : '5px 0 0', fontSize: i === 0 ? 13 : 12.5, color: i === 0 ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: i === 0 ? 600 : 400 }}>{s}</p>
        ))}
      </div>
    </div>
  );
}

// ── Bullets ──────────────────────────────────────────────────────────────────────────────────
function Bullets({ metrics }) {
  return (
    <div style={PANEL}>
      <div style={PH}>
        <h2 style={PH_H2}>Where we are vs. where the plan says we should be</h2>
        <span style={PH_SUB}>squad average per player</span>
      </div>
      {METRIC_ORDER.map(k => {
        const m = metrics[k];
        const meta = METRIC_META[k];
        const max = Math.max(m.fullWeek.hi, m.actualToDate, 1) * 1.08;
        const pc = x => `${Math.max(0, Math.min(100, (x / max) * 100))}%`;
        const color = COLOR[m.color];
        return (
          <div key={k} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 110px', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{meta.label}</div>
              <div style={{ fontSize: 10, color: 'var(--text-dimmed)' }}>{meta.unit}</div>
            </div>
            <div style={{ position: 'relative', height: 22 }} title={`plan to date ${fmtRange(m.planToDate.lo, m.planToDate.hi)} · full week ${fmtRange(m.fullWeek.lo, m.fullWeek.hi)}`}>
              <div style={{ position: 'absolute', top: 6, left: 0, right: 0, height: 10, background: 'var(--bg-app)', borderRadius: 2 }} />
              <div style={{ position: 'absolute', top: 4, height: 14, borderRadius: 2, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', left: pc(m.fullWeek.lo), width: `calc(${pc(m.fullWeek.hi)} - ${pc(m.fullWeek.lo)})` }} />
              <div style={{ position: 'absolute', top: 3, height: 16, borderRadius: 2, background: 'rgba(16,185,129,0.16)', border: '1px solid rgba(16,185,129,0.5)', left: pc(m.planToDate.lo), width: `calc(${pc(m.planToDate.hi)} - ${pc(m.planToDate.lo)})` }} />
              <div style={{ position: 'absolute', top: 7.5, height: 7, borderRadius: 2, background: color, width: pc(m.actualToDate) }} />
            </div>
            <div style={{ textAlign: 'right', fontFamily: 'ui-monospace,Menlo,Consolas,monospace' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color }}>{fmtDev(m.dev)}</div>
              <div style={{ fontSize: 10, color: 'var(--text-dimmed)' }}>{fmtNum(m.actualToDate)} vs {fmtRange(m.planToDate.lo, m.planToDate.hi)}</div>
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 10.5, color: 'var(--text-muted)', marginTop: 12 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><i style={{ width: 14, height: 7, borderRadius: 2, background: 'var(--text-primary)', display: 'inline-block' }} /> actual to date</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><i style={{ width: 14, height: 7, borderRadius: 2, background: 'rgba(16,185,129,0.3)', border: '1px solid #10b981', display: 'inline-block' }} /> plan-to-date range</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><i style={{ width: 14, height: 7, borderRadius: 2, background: 'var(--bg-app)', border: '1px solid var(--border-strong)', display: 'inline-block' }} /> full-week range</span>
      </div>
      <div style={NOTE}>Both targets are <b>ranges, not points</b> — a bar ending inside the green block is on plan; there's no "% of target" to read, only in / above / below.</div>
    </div>
  );
}

// ── Deviation chart ──────────────────────────────────────────────────────────────────────────
function DeviationChart({ metrics }) {
  const DSC = 15;
  return (
    <div style={PANEL}>
      <div style={PH}>
        <h2 style={PH_H2}>Shape of the deviation</h2>
        <span style={PH_SUB}>% outside the nearest band edge</span>
      </div>
      {METRIC_ORDER.map(k => {
        const m = metrics[k];
        const raw = m.dev;
        const clamped = Math.max(-DSC, Math.min(DSC, raw));
        const half = 50, w = Math.abs(clamped) / DSC * half;
        const left = clamped > 0 ? half : half - w;
        const color = COLOR[m.color];
        return (
          <div key={k} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 64px', alignItems: 'center', gap: 10, height: 32 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{METRIC_META[k].label}</div>
            <div style={{ position: 'relative', height: 20 }}>
              <div style={{ position: 'absolute', left: '47.5%', width: '5%', top: 0, bottom: 0, background: 'rgba(16,185,129,0.08)' }} />
              <div style={{ position: 'absolute', left: '50%', top: -4, bottom: -4, width: 1, background: 'var(--border-strong)' }} />
              {w > 0.4 && <div style={{ position: 'absolute', top: 5, height: 10, borderRadius: 2, left: `${left}%`, width: `${w}%`, background: color }} />}
            </div>
            <div style={{ textAlign: 'right', fontFamily: 'ui-monospace,Menlo,Consolas,monospace', fontSize: 12, fontWeight: 700, color }}>
              {raw === 0 ? '✓' : `${raw > 0 ? '+' : ''}${raw.toFixed(1)}%`}
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-dimmed)', margin: '2px 74px 0 122px' }}>
        <span>−15%</span><span>−7%</span><span>in band</span><span>+7%</span><span>+15%</span>
      </div>
      <div style={NOTE}>Because targets are ranges, the centre of this chart is the <em>whole band</em> — a metric inside its range has no bar, just a tick. Only genuine misses draw ink, so the pattern of what's left is the diagnosis.</div>
    </div>
  );
}

// Matches gpsWeekSummary.js's AMBER_BAND_PCT — kept as a literal here since this page doesn't
// import the backend module (see pacingDevOf's own comment above for the same reasoning).
const AMBER_TOOLTIP_PCT = 10;

// Hover tooltip for one pacing-chart point — a small SVG-native card (rect + text) rather than
// an HTML overlay, so it stays in the chart's own viewBox coordinate space and never needs a
// screen-pixel conversion. Flips to the left of the point near the chart's right edge so it's
// never clipped off the visible area.
function PacingTooltip({ point, X, Y, chartW }) {
  if (!point) return null;
  const { label, lo, hi, value, isProjected, isGrey, n } = point;
  const dev = !isGrey && hi != null && hi > 0 ? pacingDevOf(value, lo, hi) : null;
  const statusText = dev == null ? null : (dev === 0 ? 'in range' : `${dev > 0 ? '+' : ''}${dev.toFixed(1)}%`);
  const statusColor = dev == null ? 'var(--text-secondary)' : (dev === 0 ? '#10b981' : Math.abs(dev) <= AMBER_TOOLTIP_PCT ? '#f59e0b' : '#ef4444');

  const lines = isGrey
    ? [`${label} (n=${n})`, `Actual: ${fmtNum(value)}`, 'No band — descriptive only']
    : [label, `${isProjected ? 'Projected' : 'Actual'}: ${fmtNum(value)}`, `Range: ${fmtRange(lo, hi)}`, statusText].filter(Boolean);

  const boxW = 180, lineH = 15, padY = 9;
  const boxH = lines.length * lineH + padY;
  const px = X(point.i), py = Y(value);
  const flipLeft = px + boxW + 14 > chartW - 4;
  const bx = flipLeft ? px - boxW - 12 : px + 12;
  const by = Math.max(2, Math.min(280 - boxH - 2, py - boxH / 2));

  return (
    <g pointerEvents="none">
      <rect x={bx} y={by} width={boxW} height={boxH} rx={7} fill="var(--bg-card)" stroke="var(--border-strong)" strokeWidth={1} />
      {lines.map((line, idx) => {
        const isLast = idx === lines.length - 1;
        const color = isLast && statusText ? statusColor : idx === 0 ? 'var(--text-primary)' : 'var(--text-secondary)';
        return (
          <text key={idx} x={bx + 10} y={by + 15 + idx * lineH} fontSize={idx === 0 ? 11 : 10.5}
            fontWeight={idx === 0 || (isLast && statusText) ? 700 : 500} fill={color}>
            {line}
          </text>
        );
      })}
    </g>
  );
}

// ── Pacing chart (SVG) ──────────────────────────────────────────────────────────────────────
function PacingChart({ metrics, selected, onSelect, thresholdMinutes }) {
  const [hover, setHover] = useState(null);
  useEffect(() => { setHover(null); }, [selected]);
  const m = metrics[selected];
  const { rows, lastActualIdx, projValues } = useMemo(() => buildPacing(m.series), [m]);
  const grey = useMemo(() => buildGreyPacing(rows), [rows]);
  const W = 1100, H = 280, L = 64, R = 20, T = 16, B = 34;
  const iw = W - L - R, ih = H - T - B;
  const maxV = Math.max(...rows.map(r => r.hiCum || 0), ...projValues.filter(v => v != null), ...(grey ? grey.values.filter(v => v != null) : []), 1) * 1.08;
  const X = i => L + (rows.length > 1 ? (i / (rows.length - 1)) * iw : 0);
  const Y = v => T + ih - (v / maxV) * ih;
  const pathOf = (values) => {
    let d = '';
    values.forEach((v, i) => { if (v == null) return; d += `${d ? 'L' : 'M'}${X(i).toFixed(1)},${Y(v).toFixed(1)} `; });
    return d.trim();
  };
  const corridor = rows.map((r, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)},${Y(r.hiCum).toFixed(1)}`).join(' ')
    + ' ' + [...rows].reverse().map((r, j) => `L${X(rows.length - 1 - j).toFixed(1)},${Y(r.loCum).toFixed(1)}`).join(' ') + ' Z';
  const gridLines = [0, 1, 2, 3, 4].map(i => {
    const v = maxV * i / 4;
    return <React.Fragment key={i}>
      <line x1={L} y1={Y(v)} x2={W - R} y2={Y(v)} stroke="var(--border)" />
      <text x={L - 8} y={Y(v) + 4} textAnchor="end" fontSize="10.5" fill="var(--text-dimmed)" fontFamily="ui-monospace,monospace">{fmtNum(Math.round(v))}</text>
    </React.Fragment>;
  });
  const todayIdx = Math.max(0, lastActualIdx);
  const projPath = pathOf(rows.map((r, i) => (i <= lastActualIdx ? r.actCum : projValues[i])).map((v, i) => i >= lastActualIdx ? v : null));
  const actualPath = pathOf(rows.map((r, i) => i <= lastActualIdx ? r.actCum : null));

  return (
    <div style={PANEL}>
      <div style={PH}>
        <h2 style={PH_H2}>Cumulative pacing — plan vs. actual vs. projection</h2>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {METRIC_ORDER.map(k => (
            <button key={k} onClick={() => onSelect(k)}
              style={{ border: '1px solid var(--border-strong)', background: selected === k ? '#3b82f6' : 'var(--bg-card)', color: selected === k ? '#fff' : 'var(--text-secondary)', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontWeight: selected === k ? 600 : 400, fontFamily: 'inherit' }}>
              {METRIC_META[k].label}
            </button>
          ))}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
        {gridLines}
        <rect x={X(todayIdx)} y={T} width={Math.max(0, X(rows.length - 1) - X(todayIdx))} height={ih} fill="var(--bg-app)" opacity={0.5} />
        <path d={corridor} fill="rgba(16,185,129,0.13)" stroke="#10b981" strokeWidth={1} strokeOpacity={0.5} />
        <line x1={X(todayIdx)} y1={T - 4} x2={X(todayIdx)} y2={T + ih} stroke="var(--text-primary)" strokeWidth={1.5} />
        <text x={X(todayIdx) + 6} y={T + 12} fontSize="10" fill="var(--text-primary)" fontWeight="700">TODAY</text>
        {projPath && <path d={projPath} fill="none" stroke="var(--text-muted)" strokeWidth={2} strokeDasharray="3 4" />}
        {actualPath && <path d={actualPath} fill="none" stroke="var(--text-primary)" strokeWidth={2.5} />}
        {/* MD split: grey line shares the black line through MD-1, forks at MD, then runs
            parallel (permanently offset, never re-converging) for the rest of the week — see
            buildGreyPacing. Fixed neutral grey always, on purpose: this series carries no band
            state, so it must never pick up the red/green logic the primary dots use below. */}
        {grey && <path d={pathOf(grey.values)} fill="none" stroke={GREY_SERIES} strokeWidth={2} strokeOpacity={0.85} />}
        {/* Projected points — small hollow dots so the projection line has hoverable markers too,
            not just the solid actual-to-date dots. */}
        {rows.map((r, i) => {
          if (i <= lastActualIdx) return null;
          const pv = projValues[i];
          if (pv == null) return null;
          const point = { i, label: r.label, lo: r.loCum, hi: r.hiCum, value: pv, isProjected: true };
          return (
            <g key={`proj-${i}`}
              onMouseEnter={() => setHover(point)}
              onMouseLeave={() => setHover(h => (h && h.i === i && h.isProjected ? null : h))}
              style={{ cursor: 'pointer' }}>
              <circle cx={X(i)} cy={Y(pv)} r={3} fill="var(--bg-card)" stroke="var(--text-muted)" strokeWidth={1.5} />
              <circle cx={X(i)} cy={Y(pv)} r={11} fill="transparent" />
            </g>
          );
        })}
        {rows.map((r, i) => {
          if (i > lastActualIdx || r.actual == null) return null;
          const point = { i, label: r.label, lo: r.loCum, hi: r.hiCum, value: r.actCum, isProjected: false };
          return (
            <g key={i}
              onMouseEnter={() => setHover(point)}
              onMouseLeave={() => setHover(h => (h && h.i === i && !h.isProjected && !h.isGrey ? null : h))}
              style={{ cursor: 'pointer' }}>
              <circle cx={X(i)} cy={Y(r.actCum)} r={3.5} fill={r.bandPos != null && r.bandPos >= 0 && r.bandPos <= 100 ? '#10b981' : '#ef4444'} />
              <circle cx={X(i)} cy={Y(r.actCum)} r={11} fill="transparent" />
            </g>
          );
        })}
        {grey && (
          <>
            <g
              onMouseEnter={() => setHover({
                i: grey.mdIndex, label: `${rows[grey.mdIndex].label} — <${thresholdMinutes} min group`,
                value: grey.greyAtMD, isGrey: true, n: grey.n,
              })}
              onMouseLeave={() => setHover(h => (h && h.isGrey ? null : h))}
              style={{ cursor: 'pointer' }}>
              <circle cx={X(grey.mdIndex)} cy={Y(grey.greyAtMD)} r={4} fill={GREY_SERIES} stroke="var(--bg-card)" strokeWidth={1.5} />
              <circle cx={X(grey.mdIndex)} cy={Y(grey.greyAtMD)} r={11} fill="transparent" />
            </g>
            <text x={X(grey.mdIndex)} y={Y(grey.greyAtMD) + (Y(grey.greyAtMD) > Y(rows[grey.mdIndex].actCum ?? grey.greyAtMD) ? 16 : -10)} textAnchor="middle" fontSize="10" fill={GREY_SERIES} fontWeight="700">{`<${thresholdMinutes} (n=${grey.n})`}</text>
            {rows[grey.mdIndex].actual != null && (
              <text x={X(grey.mdIndex)} y={Y(rows[grey.mdIndex].actCum) - 10} textAnchor="middle" fontSize="10" fill="var(--text-primary)" fontWeight="600">{`≥${thresholdMinutes} (n=${rows[grey.mdIndex].n ?? '—'})`}</text>
            )}
          </>
        )}
        {rows.map((r, i) => (
          <text key={i} x={X(i)} y={H - 12} textAnchor="middle" fontSize="10.5" fill={i === todayIdx ? 'var(--text-primary)' : 'var(--text-dimmed)'} fontWeight={i === todayIdx ? 700 : 400}>{r.label}</text>
        ))}
        <PacingTooltip point={hover} X={X} Y={Y} chartW={W} />
      </svg>
      <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 10, fontSize: 11 }}>
        <div><div style={{ color: 'var(--text-dimmed)' }}>Actual to date</div><b style={{ fontSize: 15, fontFamily: 'ui-monospace,monospace' }}>{fmtNum(m.actualToDate)}</b></div>
        <div><div style={{ color: 'var(--text-dimmed)' }}>Plan to date</div><b style={{ fontSize: 15, fontFamily: 'ui-monospace,monospace' }}>{fmtRange(m.planToDate.lo, m.planToDate.hi)}</b></div>
        <div><div style={{ color: 'var(--text-dimmed)' }}>Status</div><b style={{ fontSize: 15, color: COLOR[m.color] }}>{fmtDev(m.dev)}</b></div>
        <div><div style={{ color: 'var(--text-dimmed)' }}>Projected week end</div><b style={{ fontSize: 15, color: COLOR[m.projectedColor], fontFamily: 'ui-monospace,monospace' }}>{fmtNum(m.projected)}</b></div>
        <div><div style={{ color: 'var(--text-dimmed)' }}>vs week band {fmtRange(m.fullWeek.lo, m.fullWeek.hi)}</div><b style={{ fontSize: 15, color: COLOR[m.projectedColor] }}>{fmtDev(m.projectedDev)}</b></div>
      </div>
      {grey && (
        <div style={NOTE}>
          <b>Match Day is split by actual minutes played</b>, not shown as one blended mean — the grey line is the {'<'}{thresholdMinutes}-minute group (unused subs, non-selected players): actual only, on purpose. It carries no band, no in-range/above/below state, and never counts toward the plan-adherence figures above, the verdict, the deviation chart, or the day × metric matrix. The black line (≥{thresholdMinutes} min) is the primary series everywhere else on this page.
        </div>
      )}
    </div>
  );
}

// ── Day × metric matrix ─────────────────────────────────────────────────────────────────────
function matrixCellStyle(v) {
  if (v == null) return { background: 'var(--bg-app)', color: 'var(--text-dimmed)' };
  if (v >= 0 && v <= 100) return { background: 'rgba(16,185,129,0.16)', color: '#6ee7b7' };
  if (v > 100) return v <= 120 ? { background: 'rgba(245,158,11,0.16)', color: '#fbbf24' } : { background: 'rgba(245,158,11,0.3)', color: '#fde68a' };
  return v >= -20 ? { background: 'rgba(245,158,11,0.16)', color: '#fbbf24' } : { background: 'rgba(239,68,68,0.2)', color: '#fca5a5' };
}
function DayMetricMatrix({ metrics, days, today }) {
  return (
    <div style={PANEL}>
      <div style={PH}>
        <h2 style={PH_H2}>Day × metric adherence</h2>
        <span style={PH_SUB}>position in that day's band · 0 = lower, 100 = upper</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 10.5, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Metric</th>
              {days.map(d => <th key={d.date} style={{ textAlign: 'center', padding: '6px 8px', fontSize: 10.5, color: 'var(--text-muted)', fontWeight: 700, opacity: d.date > today ? 0.5 : 1 }}>{d.label}</th>)}
              <th style={{ textAlign: 'center', padding: '6px 8px', fontSize: 10.5, color: 'var(--text-muted)', fontWeight: 700 }}>To date</th>
            </tr>
          </thead>
          <tbody>
            {METRIC_ORDER.map(k => {
              const m = metrics[k];
              return (
                <tr key={k}>
                  <td style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--text-primary)', borderTop: '1px solid var(--border)' }}>{METRIC_META[k].label}</td>
                  {m.series.map(d => {
                    const v = d.bandPos != null ? Math.round(d.bandPos) : null;
                    const label = v != null ? v : (d.rest ? 'rest' : d.cancelled ? '—' : d.future ? '—' : d.pending ? '…' : '—');
                    return (
                      <td key={d.date} style={{ padding: '6px 8px', textAlign: 'center', borderTop: '1px solid var(--border)', fontFamily: 'ui-monospace,monospace', opacity: d.future ? 0.5 : 1 }}>
                        <span style={{ ...matrixCellStyle(v), display: 'inline-block', borderRadius: 4, padding: '3px 6px', minWidth: 28 }}>{label}</span>
                      </td>
                    );
                  })}
                  <td style={{ padding: '6px 8px', textAlign: 'center', borderTop: '1px solid var(--border)', fontFamily: 'ui-monospace,monospace' }}>
                    {(() => { const wp = m.planToDate.hi > m.planToDate.lo ? Math.round((m.actualToDate - m.planToDate.lo) / (m.planToDate.hi - m.planToDate.lo) * 100) : null;
                      return <span style={{ ...matrixCellStyle(wp), display: 'inline-block', borderRadius: 4, padding: '3px 6px', minWidth: 28 }}>{wp ?? '—'}</span>; })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={NOTE}>Green = inside the band on that day. Day bands are narrower than the week band, so single days swing further than the cumulative picture — that's real, not noise.</div>
    </div>
  );
}

// ── Risk strip + radar ──────────────────────────────────────────────────────────────────────
function RiskAndRadar({ riskDistribution, riskDistributionHighlyImputedCount, metrics, riskFilter, onFilterChange }) {
  const cx = 130, cy = 118, r = 78, n = METRIC_ORDER.length;
  const RLO = 0.6, RHI = 1.0;
  const radiusFor = bp => bp == null ? 0.12 : Math.max(0.1, Math.min(1.3, RLO + (bp / 100) * (RHI - RLO)));
  const pt = (i, f) => { const a = -Math.PI / 2 + i * 2 * Math.PI / n; return [cx + Math.cos(a) * r * f, cy + Math.sin(a) * r * f]; };
  const poly = f => METRIC_ORDER.map((_, i) => pt(i, f).map(v => v.toFixed(1)).join(',')).join(' ');
  const actPoly = METRIC_ORDER.map((k, i) => { const m = metrics[k]; const bp = m.planToDate.hi > m.planToDate.lo ? (m.actualToDate - m.planToDate.lo) / (m.planToDate.hi - m.planToDate.lo) * 100 : null; return pt(i, radiusFor(bp)).map(v => v.toFixed(1)).join(','); }).join(' ');

  return (
    <div style={PANEL}>
      <div style={PH}>
        <h2 style={PH_H2}>Squad load-risk distribution</h2>
        <span style={PH_SUB}>reuses existing physical_risk_zone</span>
      </div>
      {riskDistributionHighlyImputedCount > 0 && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffe08a', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 11 }}>
          {riskDistributionHighlyImputedCount} player{riskDistributionHighlyImputedCount > 1 ? 's' : ''} below has a chronic window that's over 30% estimated — treat their zone as a rough guide.
        </div>
      )}
      <div style={{ display: 'flex', borderRadius: 7, overflow: 'hidden', height: 32, marginBottom: 8, cursor: 'pointer' }}>
        {Object.entries(riskDistribution).map(([zone, count]) => count > 0 && (
          <div key={zone} onClick={() => onFilterChange(riskFilter === zone ? null : zone)}
            style={{ flex: count, display: 'flex', alignItems: 'center', justifyContent: 'center', color: zone === 'noData' ? 'var(--text-secondary)' : '#fff', fontSize: 11, fontWeight: 700, background: RISK_META[zone].color, opacity: riskFilter && riskFilter !== zone ? 0.4 : 1 }}>
            {count} {RISK_META[zone].label}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 18 }}>
        Click a segment to filter the player table below. Plan adherence and injury risk are different questions.
        <br />
        <b>"No zone" isn't missing data</b> — this scoring only badges a ratio of 0.8 or higher (safe / caution / red flag); a normal or light training load is intentionally left unbadged, so most of a healthy squad shows here. See the <b>EWMA</b> page (GPS &amp; Load → Team) for every player's live ratio, badged or not.
      </div>

      <div style={PH}><h2 style={PH_H2}>Week profile</h2><span style={PH_SUB}>planned = ring</span></div>
      <svg viewBox="0 0 400 240" style={{ width: '100%', maxHeight: 240 }}>
        <polygon points={poly(RHI)} fill="rgba(16,185,129,0.1)" />
        <polygon points={poly(RLO)} fill="var(--bg-card)" />
        {[RLO, RHI].map(f => <polygon key={f} points={poly(f)} fill="none" stroke="#10b981" strokeWidth={1.2} strokeOpacity={0.6} />)}
        {METRIC_ORDER.map((_, i) => { const [x, y] = pt(i, 1.15); return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--border)" />; })}
        <polygon points={actPoly} fill="rgba(59,130,246,0.12)" stroke="#3b82f6" strokeWidth={2} />
        {METRIC_ORDER.map((k, i) => { const m = metrics[k]; const bp = m.planToDate.hi > m.planToDate.lo ? (m.actualToDate - m.planToDate.lo) / (m.planToDate.hi - m.planToDate.lo) * 100 : null; const [x, y] = pt(i, radiusFor(bp)); return <circle key={i} cx={x} cy={y} r={3.4} fill={COLOR[m.color]} />; })}
        {METRIC_ORDER.map((k, i) => { const [x, y] = pt(i, 1.32); return <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="10" fill="var(--text-muted)" fontWeight="600">{METRIC_META[k].short}</text>; })}
      </svg>
    </div>
  );
}

// ── Top-speed exposure ──────────────────────────────────────────────────────────────────────
function TopSpeedExposure({ players, thresholdPct }) {
  const elig = players.filter(p => p.topSpeed.eligible);
  const hit = elig.filter(p => p.topSpeed.exposed);
  const miss = elig.filter(p => !p.topSpeed.exposed);
  const na = players.length - elig.length;
  return (
    <div style={PANEL}>
      <div style={PH}>
        <h2 style={PH_H2}>Top-speed exposure</h2>
        <span style={PH_SUB}>pass / fail per player — not a cumulative load target</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '.85fr 1.15fr', gap: 30 }}>
        <div>
          <div style={{ fontFamily: 'ui-monospace,monospace', fontSize: 34, fontWeight: 700 }}>{hit.length}<span style={{ fontSize: 16, color: 'var(--text-dimmed)', fontWeight: 400, marginLeft: 4 }}>/ {elig.length}</span></div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>players reached <b>≥{thresholdPct}% of their season-best speed</b> this week</div>
          <div style={{ display: 'flex', borderRadius: 7, overflow: 'hidden', height: 28, marginTop: 12 }}>
            {hit.length > 0 && <div style={{ flex: hit.length, background: '#10b981', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{hit.length} exposed</div>}
            {miss.length > 0 && <div style={{ flex: miss.length, background: '#ef4444', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{miss.length} not yet</div>}
            {na > 0 && <div style={{ flex: na, background: 'var(--border-strong)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{na} n/a</div>}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700, marginBottom: 6 }}>Not yet exposed</div>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <tbody>
              {miss.concat(players.filter(p => p.onIndividualPlan && p.topSpeed.eligible === false)).slice(0, 12).map(p => (
                <tr key={p.playerId}>
                  <td style={{ padding: '5px 6px', fontWeight: 600, borderTop: '1px solid var(--border)' }}>{p.name}</td>
                  <td style={{ padding: '5px 6px', color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>{p.position}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right', color: 'var(--text-dimmed)', borderTop: '1px solid var(--border)' }}>{p.topSpeed.pctOfBest != null ? `best ${p.topSpeed.pctOfBest.toFixed(1)}%` : '—'}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right', borderTop: '1px solid var(--border)' }}>
                    {p.topSpeed.daysSinceLastExposure != null
                      ? <b style={{ color: p.topSpeed.daysSinceLastExposure >= 10 ? '#ef4444' : '#f59e0b' }}>{p.topSpeed.daysSinceLastExposure}d</b>
                      : <span style={{ color: 'var(--text-dimmed)' }}>never</span>}
                  </td>
                  <td style={{ padding: '5px 6px', borderTop: '1px solid var(--border)' }}>{p.onIndividualPlan && <Chip tone="mod">modified</Chip>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div style={NOTE}>Max speed doesn't accumulate and has no weekly range — either a player got near his ceiling this week or he didn't, treated as a binary check so it can't be read as a load metric. Reference is each player's <b>season best</b> (June-15 season boundary), which is self-raising through the season.</div>
    </div>
  );
}

function Chip({ children, tone }) {
  const tones = {
    mod: { background: 'rgba(59,130,246,0.15)', color: '#60a5fa' },
    out: { background: 'rgba(239,68,68,0.15)', color: '#f87171' },
    default: { background: 'var(--border)', color: 'var(--text-secondary)' },
  };
  const t = tones[tone] || tones.default;
  return <span style={{ display: 'inline-block', fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', padding: '2px 6px', borderRadius: 4, ...t }}>{children}</span>;
}

function BandCell({ v, muted }) {
  if (v == null) return <span style={{ color: 'var(--text-dimmed)' }}>—</span>;
  const color = muted ? 'var(--text-dimmed)' : (v >= 0 && v <= 100 ? '#10b981' : (v >= -20 && v <= 120 ? '#f59e0b' : '#ef4444'));
  const clamped = Math.max(-45, Math.min(145, v));
  const left = (clamped + 50) / 200 * 100;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ position: 'relative', display: 'inline-block', width: 42, height: 8, background: 'var(--bg-app)', borderRadius: 2 }}>
        <span style={{ position: 'absolute', left: '25%', width: '50%', top: 0, bottom: 0, background: 'rgba(16,185,129,0.18)', borderLeft: '1px solid rgba(16,185,129,0.5)', borderRight: '1px solid rgba(16,185,129,0.5)' }} />
        <span style={{ position: 'absolute', top: -2, width: 3, height: 12, borderRadius: 2, background: color, left: `${left}%`, transform: 'translateX(-1.5px)' }} />
      </span>
      <b style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11, color, minWidth: 22, textAlign: 'right' }}>{Math.round(v)}</b>
    </span>
  );
}

// ── Player table (Stage 4's null-last comparator, per-page copy — same convention as
// TeamKpiOverview.js / Team360Table.js). ─────────────────────────────────────────────────────
function compareRows(a, b, key, dir) {
  const av = a[key], bv = b[key];
  const aNull = av === null || av === undefined;
  const bNull = bv === null || bv === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  return dir === 'asc' ? av - bv : bv - av;
}
function SortHeader({ label, sortKey, activeKey, dir, onClick, align }) {
  const active = sortKey === activeKey;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th onClick={() => onClick(sortKey)} style={{ textAlign: align || 'left', padding: '8px 10px', fontSize: 10.5, fontWeight: 700, color: active ? 'var(--text-primary)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: align === 'center' ? 'center' : 'flex-start' }}>{label} <Icon size={11} /></span>
    </th>
  );
}
function PlayerTable({ players, riskFilter }) {
  const [denominator, setDenominator] = useState('available');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const rows = useMemo(() => players.map(p => {
    const m = p.metrics[denominator];
    const flat = { playerId: p.playerId, name: p.name, position: p.position, sessions: p.sessionsAttended, riskZone: p.riskZone, onIndividualPlan: p.onIndividualPlan, isOut: p.isOut, topSpeed: p.topSpeed };
    METRIC_ORDER.forEach(k => { flat[k] = m[k]?.bandPos ?? null; });
    flat.flagged = !p.onIndividualPlan && !p.isOut && METRIC_ORDER.some(k => flat[k] != null && Math.abs(flat[k] - 100) > 20 && (flat[k] < 0 || flat[k] > 100));
    return flat;
  }), [players, denominator]);

  const filtered = useMemo(() => rows
    .filter(r => !riskFilter || (riskFilter === 'noData' ? !r.riskZone : r.riskZone === riskFilter))
    .filter(r => !flaggedOnly || r.flagged), [rows, riskFilter, flaggedOnly]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => compareRows(a, b, sortKey, sortDir)), [filtered, sortKey, sortDir]);

  const toggleSort = key => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('asc'); } };

  return (
    <div style={PANEL}>
      <div style={PH}>
        <h2 style={PH_H2}>Per player — position inside his own positional band</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={PH_SUB}>0 = lower bound · 100 = upper bound</span>
          <Seg value={denominator} onChange={setDenominator} options={[{ value: 'available', label: 'vs. his available days' }, { value: 'squad', label: 'vs. full squad plan' }]} />
          <Seg value={flaggedOnly} onChange={setFlaggedOnly} options={[{ value: false, label: 'All' }, { value: true, label: 'Flagged only' }]} />
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: 'var(--bg-app)' }}>
              <SortHeader label="Player" sortKey="name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortHeader label="Pos" sortKey="position" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
              <SortHeader label="Sessions" sortKey="sessions" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="center" />
              {METRIC_ORDER.map(k => <SortHeader key={k} label={METRIC_META[k].short} sortKey={k} activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="center" />)}
              <th style={{ textAlign: 'center', padding: '8px 10px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }} title="Binary: did he reach the exposure threshold this week?">Top speed</th>
              <th style={{ textAlign: 'center', padding: '8px 10px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Load risk</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => {
              const mod = r.onIndividualPlan || r.isOut;
              return (
                <tr key={r.playerId} style={{ background: r.flagged ? 'rgba(245,158,11,0.05)' : 'transparent' }}>
                  <td style={{ padding: '7px 10px', fontWeight: 600, color: 'var(--text-primary)', borderTop: '1px solid var(--border)' }}>{r.name}</td>
                  <td style={{ padding: '7px 10px', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}>{r.position}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'center', fontFamily: 'ui-monospace,monospace', borderTop: '1px solid var(--border)' }}>{r.sessions}</td>
                  {METRIC_ORDER.map(k => <td key={k} style={{ padding: '7px 10px', textAlign: 'center', borderTop: '1px solid var(--border)' }}><BandCell v={r[k]} muted={mod} /></td>)}
                  <td style={{ padding: '7px 10px', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
                    {r.topSpeed.eligible === false ? <span style={{ color: 'var(--text-dimmed)' }}>—</span> :
                      <span title={`${r.topSpeed.pctOfBest?.toFixed(1)}% of season best`} style={{ display: 'inline-flex', width: 20, height: 20, borderRadius: 5, alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, background: r.topSpeed.exposed ? 'rgba(16,185,129,0.18)' : 'rgba(239,68,68,0.18)', color: r.topSpeed.exposed ? '#10b981' : '#ef4444' }}>{r.topSpeed.exposed ? '✓' : '✗'}</span>}
                  </td>
                  <td style={{ padding: '7px 10px', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
                    {r.riskZone ? <Chip tone={r.riskZone === 'red_flag' ? 'out' : r.riskZone === 'caution' ? 'mod' : 'default'}>{RISK_META[r.riskZone].label}</Chip> : <Chip>no zone</Chip>}
                  </td>
                  <td style={{ padding: '7px 10px', borderTop: '1px solid var(--border)' }}>
                    {r.isOut ? <Chip tone="out">out</Chip> : r.onIndividualPlan ? <Chip tone="mod">modified</Chip> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={NOTE}>
        <b>Every player is judged against his own positional range</b>, not the squad average. <b>Denominator matters</b> — a player who missed sessions is compared against the plan for the days he was available, not the full week, unless you switch the toggle above. Players on an individualised plan are excluded from squad aggregates and never colour-coded against the squad scenario.
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────────────────────
export default function WeekSummary({ user }) {
  const [scenarios, setScenarios] = useState([]);
  // null = "current week" (a real, meaningful state here, not just "unset") — kept as
  // number-or-null at every call site by coercing at the URL boundary.
  const [scenarioIdRaw, setScenarioIdRaw] = useUrlParam('scenario_id', '');
  const scenarioId = scenarioIdRaw === '' ? null : Number(scenarioIdRaw);
  const setScenarioId = (id) => setScenarioIdRaw(id == null ? '' : id);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pacingMetric, setPacingMetric] = useState('TD');
  const [riskFilter, setRiskFilter] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { getGpsWeekSummaryScenarios().then(setScenarios).catch(() => setScenarios([])); }, []);

  useEffect(() => {
    setLoading(true);
    getGpsWeekSummary(scenarioId || undefined)
      .then(d => { setData(d); setRiskFilter(null); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [scenarioId]);

  // Same captureBlocks/packBlocksIntoPages/A4-assembly pipeline as the Physical Report's PDF
  // export (src/pdfExport.js, extracted from PhysicalReportExportModal.js) — this page's panels
  // are marked with the same data-report-block/data-force-page convention rather than a second
  // export mechanism. Dark background passed through since this page (unlike the Physical
  // Report) renders in this app's dark theme, not on a white report background.
  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const container = document.getElementById('week-summary-export-root');
      const arrayBuffer = await renderReportPdf(container, '#0b0f14', { scale: 1.5, imageFormat: 'JPEG', imageQuality: 0.85 });
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      downloadBlob(blob, `week-summary-${data?.scenario?.scenario || 'export'}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;
  if (!data || !data.scenario) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>No team scenario found — create one in the Upload zone first.</div>;

  const currentIdx = scenarios.findIndex(s => s.id === data.scenario.id);
  const goPrevious = () => { const next = scenarios[currentIdx + 1]; if (next) setScenarioId(next.id); };
  const goCurrent = () => setScenarioId(null);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }} id="week-summary-export-root">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Week Summary</h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Squad performance against the active scenario's targets</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={goPrevious} disabled={currentIdx < 0 || currentIdx >= scenarios.length - 1}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 12, cursor: currentIdx < scenarios.length - 1 ? 'pointer' : 'default', opacity: currentIdx >= scenarios.length - 1 || currentIdx < 0 ? 0.4 : 1 }}>Previous</button>
          <button onClick={goCurrent} disabled={!scenarioId}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: !scenarioId ? '#3b82f6' : 'var(--bg-card)', color: !scenarioId ? '#fff' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Current week</button>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setPickerOpen(o => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' }}>
              Pick scenario… <ChevronDown size={12} />
            </button>
            {pickerOpen && (
              <div style={{ position: 'absolute', top: '110%', right: 0, background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 10, maxHeight: 280, overflowY: 'auto', minWidth: 200 }}>
                {scenarios.map(s => (
                  <button key={s.id} onClick={() => { setScenarioId(s.id); setPickerOpen(false); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--border)', background: s.id === data.scenario.id ? 'var(--bg-app)' : 'transparent', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}>
                    {s.scenario} <span style={{ color: 'var(--text-dimmed)', fontSize: 10.5 }}>{fmtShortDate(s.startDate)}–{fmtShortDate(s.endDate)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={handleExportPdf} disabled={exporting}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 12, cursor: exporting ? 'default' : 'pointer' }}>
            <FileDown size={13} /> {exporting ? 'Exporting…' : 'Export PDF'}
          </button>
        </div>
      </div>

      <div data-report-block="true" data-force-page="true">
        <ScenarioStrip data={data} />
        <VerdictBanner metrics={data.metrics} />
      </div>

      <div data-report-block="true" style={{ display: 'grid', gridTemplateColumns: '1.15fr .85fr', gap: 14 }}>
        <Bullets metrics={data.metrics} />
        <DeviationChart metrics={data.metrics} />
      </div>

      <div data-report-block="true">
        <PacingChart metrics={data.metrics} selected={pacingMetric} onSelect={setPacingMetric} thresholdMinutes={data.mdSplit?.thresholdMinutes ?? 60} />
      </div>

      <div data-report-block="true" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <DayMetricMatrix metrics={data.metrics} days={data.days} today={data.today} />
        <RiskAndRadar riskDistribution={data.riskDistribution} riskDistributionHighlyImputedCount={data.riskDistributionHighlyImputedCount} metrics={data.metrics} riskFilter={riskFilter} onFilterChange={setRiskFilter} />
      </div>

      <div data-report-block="true">
        <TopSpeedExposure players={data.players} thresholdPct={data.thresholdPct} />
      </div>
      <div data-report-block="true">
        <PlayerTable players={data.players} riskFilter={riskFilter} />
      </div>
    </div>
  );
}
